import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { del, get, patch, post } from '../lib/api';
import { Avatar, ErrorNote, Empty, Sheet, Spinner } from '../components/ui';
import ChoreEditor from '../components/ChoreEditor';
import HistoryTable from '../components/HistoryTable';
import { KidBoardView, useBoard } from './KidHome';
import { WEEKDAYS, frequencyLabel, weekdayName } from '../lib/format';
import PayoutSheet from '../components/PayoutSheet';
import type { Child, Chore, PeriodSummary, QuietDay, StreakView } from '../lib/types';

type Tab = 'today' | 'chores' | 'activities' | 'rules' | 'history';
type Row = Omit<PeriodSummary, 'items'>;

const TABS: { id: Tab; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'chores', label: 'Chores' },
  { id: 'activities', label: 'Activities' },
  { id: 'rules', label: 'Rules' },
  { id: 'history', label: 'History' },
];

export default function ParentChild(): JSX.Element {
  const childId = Number(useParams().id);
  const [tab, setTab] = useState<Tab>('today');
  const { board, error, toggle, snooze } = useBoard(childId);

  if (error && !board) return <ErrorNote error={error} />;
  if (!board) return <Spinner />;

  return (
    <>
      <div className="appbar">
        <Link className="btn ghost" to="/">
          ‹ Family
        </Link>
      </div>
      <div className="row">
        <Avatar emoji={board.child.emoji} large />
        <div>
          <h1 style={{ marginBottom: 0 }}>{board.child.name}</h1>
          <div className="tiny muted">
            {board.quietToday ? `🏅 ${board.quietToday} today` : 'No activity today'}
            {' · '}🔥 {board.streak.days} {board.streak.days === 1 ? 'day' : 'days'}
            {board.streak.best > board.streak.days && ` (best ${board.streak.best})`}
            {' · '}🛡️ {board.streak.shields}
          </div>
        </div>
      </div>

      <div className="chips scroll" style={{ margin: '14px 0' }}>
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={`chip${tab === entry.id ? ' on' : ''}`}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'today' && <KidBoardView board={board} onToggle={toggle} onSnooze={snooze} error={error} />}
      {tab === 'chores' && <ChoresTab childId={childId} />}
      {tab === 'activities' && <ActivitiesTab childId={childId} />}
      {tab === 'rules' && <RulesTab child={board.child} />}
      {tab === 'history' && (
        <>
          <StreakCard childId={childId} name={board.child.name} />
          <HistoryTab childId={childId} />
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ chores */

function ChoresTab({ childId }: { childId: number }): JSX.Element {
  const [chores, setChores] = useState<Chore[] | null>(null);
  const [editing, setEditing] = useState<Chore | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    get<{ chores: Chore[] }>(`/api/chores?childId=${childId}`)
      .then((res) => setChores(res.chores))
      .catch((err) => setError((err as Error).message));
  }, [childId]);

  useEffect(load, [load]);

  if (!chores) return <Spinner />;

  return (
    <>
      <ErrorNote error={error} />
      {chores.length === 0 && <Empty icon="🧹">No chores yet.</Empty>}
      <div className="stack">
        {chores.map((chore) => (
          <button
            key={chore.id}
            type="button"
            className="chore"
            onClick={() => setEditing(chore)}
          >
            <span className="chore-icon">{chore.icon}</span>
            <span className="grow">
              <span className="chore-title">{chore.title}</span>
              <span className="row wrap tiny muted" style={{ gap: 6, marginTop: 2 }}>
                <span>{describe(chore)}</span>
                {chore.points > 1 && <span className="badge">{chore.points} pts</span>}
                {chore.counts_for_pay === 0 && <span className="badge">Bonus</span>}
              </span>
            </span>
            <span className="muted">›</span>
          </button>
        ))}
      </div>
      <button className="btn block" type="button" style={{ marginTop: 14 }} onClick={() => setCreating(true)}>
        + Add chore
      </button>
      {(creating || editing) && (
        <ChoreEditor
          childId={childId}
          chore={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={load}
        />
      )}
    </>
  );
}

function describe(chore: Chore): string {
  if (chore.frequency === 'daily') {
    const days = chore.weekdays.split(',').map(Number);
    if (days.length === 7) return 'Every day';
    return days.map((d) => weekdayName(d)).join(', ');
  }
  if (chore.frequency === 'monthly') return `Monthly · by the ${chore.due_day_of_month}${ordinal(chore.due_day_of_month)}`;
  return `${frequencyLabel(chore.frequency)} · by ${weekdayName(chore.due_weekday, true)}`;
}

function ordinal(n: number): string {
  if (n % 100 >= 11 && n % 100 <= 13) return 'th';
  return ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
}

/* -------------------------------------------------------------- activities */

function ActivitiesTab({ childId }: { childId: number }): JSX.Element {
  const [days, setDays] = useState<QuietDay[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<'weekly' | 'date'>('weekly');
  const [weekday, setWeekday] = useState(2);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(() => {
    get<{ quietDays: QuietDay[] }>(`/api/quiet-days?childId=${childId}`)
      .then((res) => setDays(res.quietDays))
      .catch((err) => setError((err as Error).message));
  }, [childId]);

  useEffect(load, [load]);

  async function add() {
    setError(null);
    try {
      await post('/api/quiet-days', {
        childId,
        label: label.trim() || 'Activity',
        kind,
        ...(kind === 'weekly' ? { weekday } : { date }),
      });
      setAdding(false);
      setLabel('');
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function remove(id: number) {
    await del(`/api/quiet-days/${id}`);
    load();
  }

  if (!days) return <Spinner />;

  return (
    <>
      <ErrorNote error={error} />
      <p className="small muted">
        Sports and other activities. Chores still count on these days, but no reminders are sent.
      </p>
      {days.length === 0 && <Empty icon="🏐">No activity days yet.</Empty>}
      <div className="stack">
        {days.map((day) => (
          <div key={day.id} className="card tight row between">
            <div>
              <strong>{day.label}</strong>
              <div className="tiny muted">
                {day.kind === 'weekly' ? `Every ${weekdayName(day.weekday ?? 1, true)}` : day.date}
              </div>
            </div>
            <button className="btn ghost" type="button" onClick={() => void remove(day.id)}>
              Remove
            </button>
          </div>
        ))}
      </div>
      <button className="btn block" type="button" style={{ marginTop: 14 }} onClick={() => setAdding(true)}>
        + Add activity day
      </button>

      {adding && (
        <Sheet title="Activity day" onClose={() => setAdding(false)}>
          <label className="field">
            <span>What is it?</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Handball training" />
          </label>
          <div className="chips" style={{ marginBottom: 14 }}>
            <button type="button" className={`chip${kind === 'weekly' ? ' on' : ''}`} onClick={() => setKind('weekly')}>
              Every week
            </button>
            <button type="button" className={`chip${kind === 'date' ? ' on' : ''}`} onClick={() => setKind('date')}>
              One date
            </button>
          </div>
          {kind === 'weekly' ? (
            <label className="field">
              <span>Day</span>
              <select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
                {WEEKDAYS.map((day) => (
                  <option key={day.value} value={day.value}>
                    {day.long}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="field">
              <span>Date</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
          )}
          <button className="btn block" type="button" onClick={add}>
            Add
          </button>
        </Sheet>
      )}
    </>
  );
}

/* ------------------------------------------------------------------- rules */

function RulesTab({ child }: { child: Child }): JSX.Element {
  const [weekly, setWeekly] = useState(child.allowance_weekly);
  const [monthly, setMonthly] = useState(child.allowance_monthly);
  const [threshold, setThreshold] = useState(Math.round(child.qualify_threshold * 100));
  const [mode, setMode] = useState(child.payout_mode);
  const [times, setTimes] = useState<string[]>(() => {
    try {
      return JSON.parse(child.reminder_times) as string[];
    } catch {
      return ['16:00'];
    }
  });
  const [enabled, setEnabled] = useState(child.reminders_enabled === 1);
  const [lead, setLead] = useState(child.nudge_lead_days);
  const [headStart, setHeadStart] = useState(child.head_start_points);
  const [reminderMode, setReminderMode] = useState(child.reminder_mode);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    setSaved(false);
    try {
      await patch(`/api/users/${child.id}`, {
        allowance_weekly: weekly,
        allowance_monthly: monthly,
        qualify_threshold: Math.min(100, Math.max(0, threshold)) / 100,
        payout_mode: mode,
        reminder_times: times.filter((t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t)),
        reminders_enabled: enabled,
        nudge_lead_days: lead,
        head_start_points: headStart,
        reminder_mode: reminderMode,
      });
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <>
      <ErrorNote error={error} />
      <div className="card">
        <h3>Pocket money</h3>
        <label className="field">
          <span>Weekly allowance (0 = none)</span>
          <input type="number" min={0} value={weekly} onChange={(e) => setWeekly(Number(e.target.value))} />
        </label>
        <label className="field">
          <span>Monthly allowance (0 = none)</span>
          <input type="number" min={0} value={monthly} onChange={(e) => setMonthly(Number(e.target.value))} />
        </label>
        <label className="field">
          <span>Chores needed to qualify (%)</span>
          <input
            type="number"
            min={0}
            max={100}
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
          />
        </label>
        <label className="field">
          <span>Head start each period (points)</span>
          <input
            type="number"
            min={0}
            max={20}
            value={headStart}
            onChange={(e) => setHeadStart(Number(e.target.value))}
          />
        </label>
        <p className="tiny muted" style={{ marginTop: -8 }}>
          The bar opens part-filled and the target grows to match, so it is real credit rather than a trick. A
          part-filled bar gets finished far more often than an empty one. Set 0 to turn it off.
        </p>
        <label className="field">
          <span>If they fall short</span>
          <select value={mode} onChange={(e) => setMode(e.target.value as Child['payout_mode'])}>
            <option value="all_or_nothing">No pocket money</option>
            <option value="prorated">Pay the share they did</option>
          </select>
        </label>
      </div>

      <div className="card">
        <h3>Reminders</h3>
        <div className="toggle">
          <span className="small">Send reminders to {child.name}</span>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        </div>
        <div className="chips" style={{ margin: '8px 0 12px' }}>
          <button
            type="button"
            className={`chip${reminderMode === 'smart' ? ' on' : ''}`}
            onClick={() => setReminderMode('smart')}
          >
            When they usually start
          </button>
          <button
            type="button"
            className={`chip${reminderMode === 'fixed' ? ' on' : ''}`}
            onClick={() => setReminderMode('fixed')}
          >
            At fixed times
          </button>
        </div>
        <p className="tiny muted" style={{ marginTop: -4 }}>
          {reminderMode === 'smart'
            ? `The app watches when ${child.name} actually gets going and nudges 20 minutes before, falling back to the times below until it has learned.`
            : 'Reminders go out at the times below. At most two a day, and none when nothing is due.'}
        </p>
        <div className="stack" style={{ marginTop: 8 }}>
          {times.map((time, index) => (
            <div key={index} className="row">
              <input
                type="time"
                value={time}
                onChange={(e) =>
                  setTimes((current) => current.map((t, i) => (i === index ? e.target.value : t)))
                }
              />
              <button
                className="btn ghost"
                type="button"
                onClick={() => setTimes((current) => current.filter((_, i) => i !== index))}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        {times.length < 4 && (
          <button
            className="btn secondary small"
            type="button"
            style={{ marginTop: 8 }}
            onClick={() => setTimes((current) => [...current, '17:00'])}
          >
            + Add a reminder time
          </button>
        )}
        <label className="field" style={{ marginTop: 14 }}>
          <span>Warn about weekly/monthly chores this many days ahead</span>
          <input
            type="number"
            min={0}
            max={14}
            value={lead}
            onChange={(e) => setLead(Number(e.target.value))}
          />
        </label>
      </div>

      <button className="btn block" type="button" style={{ marginTop: 14 }} onClick={save}>
        {saved ? 'Saved ✓' : 'Save rules'}
      </button>
    </>
  );
}

/* ----------------------------------------------------------------- history */

/**
 * The last fortnight, and a way to give a day back. For "he did it, he just
 * forgot to tick it" — or for a day the app itself got wrong. Only days that
 * are actually breaking the run can be restored, and a restored day is on the
 * record as a parent's decision, not passed off as a shield.
 */
function StreakCard({ childId, name }: { childId: number; name: string }): JSX.Element {
  const [view, setView] = useState<StreakView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    get<StreakView>(`/api/streak?childId=${childId}`)
      .then(setView)
      .catch((err) => setError((err as Error).message));
  }, [childId]);
  useEffect(load, [load]);

  async function restore(date: string) {
    const label = new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
    if (!confirm(`Count ${label} towards ${name}'s streak? It will show as restored by a parent.`)) return;
    setBusy(date);
    setError(null);
    try {
      await post('/api/streak/restore', { childId, date });
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  if (!view) return <div className="card"><Spinner /></div>;
  const broken = view.recent.filter((day) => day.breaksRun);
  const restored = view.recent.filter((day) => day.saved === 'parent');

  return (
    <div className="card">
      <div className="row between">
        <h3 style={{ margin: 0 }}>Streak</h3>
        <span className="tiny muted">
          🔥 {view.days} · best {view.best} · 🛡️ {view.shields}
        </span>
      </div>
      <ErrorNote error={error} />
      {broken.length === 0 ? (
        <p className="tiny muted" style={{ marginBottom: 0 }}>Nothing broken in the last two weeks.</p>
      ) : (
        <div className="stack" style={{ marginTop: 10 }}>
          {broken.map((day) => (
            <div key={day.date} className="row between">
              <span className="small">
                {new Date(`${day.date}T00:00:00Z`).toLocaleDateString(undefined, {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  timeZone: 'UTC',
                })}
                <span className="tiny muted" style={{ display: 'block' }}>
                  {day.done}/{day.due} done{day.sealed ? ' · too late for them to fix' : ' · they can still fix this today'}
                </span>
              </span>
              <button className="btn small" type="button" disabled={busy === day.date} onClick={() => void restore(day.date)}>
                {busy === day.date ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          ))}
        </div>
      )}
      {restored.length > 0 && (
        <p className="tiny muted" style={{ marginBottom: 0 }}>
          Restored by a parent: {restored.map((day) => day.date).join(', ')}
        </p>
      )}
    </div>
  );
}

function HistoryTab({ childId }: { childId: number }): JSX.Element {
  const [periodType, setPeriodType] = useState<'week' | 'month'>('week');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [picked, setPicked] = useState<Row | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setRows(null);
    get<{ history: Row[] }>(`/api/history?childId=${childId}&periodType=${periodType}&count=12`)
      .then((res) => setRows(res.history))
      .catch((err) => setError((err as Error).message));
  }, [childId, periodType]);

  useEffect(load, [load]);

  return (
    <>
      <ErrorNote error={error} />
      <div className="chips" style={{ marginBottom: 12 }}>
        {(['week', 'month'] as const).map((option) => (
          <button
            key={option}
            type="button"
            className={`chip${periodType === option ? ' on' : ''}`}
            onClick={() => setPeriodType(option)}
          >
            {option === 'week' ? 'Weeks' : 'Months'}
          </button>
        ))}
      </div>
      <div className="card">
        {rows ? <HistoryTable rows={rows} periodType={periodType} onPick={setPicked} /> : <Spinner />}
      </div>
      {picked && (
        <PayoutSheet
          childId={childId}
          row={picked}
          onClose={() => setPicked(null)}
          onSaved={() => {
            setPicked(null);
            load();
          }}
        />
      )}
    </>
  );
}
